// src/services/organizationService.js
import { apiRequest } from './api';

export const organizationService = {
    // Get organizations list (Core Admin only)
    getOrganizations: async (params = {}) => {
        try {
            const response = await apiRequest.get('/organizations', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organizations');
        }
    },

    // Get single organization details
    getOrganizationById: async (orgId) => {
        try {
            const response = await apiRequest.get(`/organizations/${orgId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organization details');
        }
    },

    // Alias for getOrganizationById
    getOrganization: async (orgId) => {
        return await organizationService.getOrganizationById(orgId);
    },

    // Create new organization (Core Admin only)
    createOrganization: async (orgData) => {
        try {
            const response = await apiRequest.post('/organizations', orgData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create organization');
        }
    },

    // Update organization
    updateOrganization: async (orgId, orgData) => {
        try {
            const response = await apiRequest.put(`/organizations/${orgId}`, orgData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update organization');
        }
    },

    // Update organization logo
    updateOrganizationLogo: async (orgId, formDataOrBase64) => {
        try {
            let response;

            // Support both FormData (for file upload) and base64 string
            if (formDataOrBase64 instanceof FormData) {
                // Use POST for multipart/form-data uploads (PHP standard)
                response = await apiRequest.post('/organizations/logo', formDataOrBase64, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                // Base64 string - use PUT
                response = await apiRequest.put('/organizations/logo', {
                    org_id: orgId,
                    logo_base64: formDataOrBase64
                });
            }

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update organization logo');
        }
    },

    // Delete organization (Core Admin only)
    deleteOrganization: async (orgId) => {
        try {
            const response = await apiRequest.delete(`/organizations/${orgId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete organization');
        }
    },

    // Suspend/Activate organization
    toggleOrganizationStatus: async (orgId, status) => {
        try {
            const response = await apiRequest.put(`/organizations/${orgId}/status`, { status });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update organization status');
        }
    },

    updatePremisesStatus: async (premisesId, status) => {
        try {
            const response = await apiRequest.put('/premises/status', {
                premises_id: premisesId,
                status: status
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update premises status');
        }
    },
    // Get designations for organization category
    // Update the getDesignations method to include premises_id
    getDesignations: async (orgCategoryId, premisesId = null) => {
        try {
            const params = { org_category_id: orgCategoryId };
            if (premisesId) {
                params.premises_id = premisesId;
            }

            const response = await apiRequest.get('/designations', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch designations');
        }
    },

    // Update the bulk upload staff method
    bulkUploadStaff: async (orgId, premisesId, fileData, imageFiles = [], uploadMethod = 'separate') => {
        try {
            const formData = new FormData();
            formData.append('org_id', orgId);
            formData.append('premises_id', premisesId); // Add premises_id
            formData.append('upload_method', uploadMethod);

            if (uploadMethod === 'zip') {
                formData.append('zip_file', fileData);
            } else {
                formData.append('csv_file', fileData);
                imageFiles.forEach((file) => {
                    formData.append('image_files[]', file);
                });
            }

            const response = await apiRequest.post('/staff/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload staff bulk data');
        }
    },

    // Get all premises across all organizations (Core Admin only)
    getAllPremises: async (params = {}) => {
        try {
            const response = await apiRequest.get('/premises/all', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch all premises');
        }
    },

    // Bulk upload staff
    bulkUploadStaff: async (orgId, premisesId, fileData, imageFiles = [], uploadMethod = 'separate') => {
        try {
            const formData = new FormData();
            formData.append('org_id', orgId);
            formData.append('premises_id', premisesId); // Add premises_id
            formData.append('upload_method', uploadMethod);

            if (uploadMethod === 'zip') {
                formData.append('zip_file', fileData);
            } else {
                formData.append('csv_file', fileData);
                imageFiles.forEach((file) => {
                    formData.append('image_files[]', file);
                });
            }

            const response = await apiRequest.post('/staff/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload staff bulk data');
        }
    },
    // Bulk upload premises for organization
    bulkUploadPremises: async (orgId, fileData, logoFiles = [], uploadMethod = 'separate') => {
        try {
            const formData = new FormData();
            formData.append('org_id', orgId);
            formData.append('upload_method', uploadMethod);

            if (uploadMethod === 'zip') {
                formData.append('zip_file', fileData);
            } else {
                formData.append('csv_file', fileData);
                logoFiles.forEach((file) => {
                    formData.append('logo_files[]', file);
                });
            }

            const response = await apiRequest.post('/organizations/premises/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload premises bulk data');
        }
    },

    // NEW: Bulk upload premises for the logged-in client admin's organization
    bulkUploadOrganizationPremises: async (fileData, logoFiles = [], uploadMethod = 'separate') => {
        try {
            const formData = new FormData();
            // Note: org_id is not added here for client_admin.
            // The backend endpoint '/organizations/premises/bulk' should
            // derive the org_id from the authenticated user's session
            // when the user_type is 'client_admin'.
            formData.append('upload_method', uploadMethod);

            if (uploadMethod === 'zip') {
                formData.append('zip_file', fileData);
            } else {
                formData.append('csv_file', fileData);
                logoFiles.forEach((file) => {
                    formData.append('logo_files[]', file);
                });
            }

            // Call the same backend endpoint
            const response = await apiRequest.post('/organizations/premises/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to upload premises bulk data');
        }
    },

    // PREMISES MANAGEMENT FOR ORGANIZATIONS (Core Admin only)

    // Get organization premises
    getOrganizationPremises: async (orgId, params = {}) => {
        try {
            const response = await apiRequest.get('/organizations/premises', {
                params: { ...params, org_id: orgId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organization premises');
        }
    },

    // Create premises for organization
    createOrganizationPremises: async (premisesData) => {
        try {
            const response = await apiRequest.post('/organizations/premises', premisesData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create premises');
        }
    },

    // Update premises in organization
    updateOrganizationPremises: async (premisesId, premisesData) => {
        try {
            const response = await apiRequest.put('/organizations/premises', premisesData, {
                params: { premises_id: premisesId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update premises');
        }
    },

    // Delete premises from organization
    deleteOrganizationPremises: async (premisesId) => {
        try {
            const response = await apiRequest.delete('/organizations/premises', {
                params: { premises_id: premisesId }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete premises');
        }
    },

    // Get premises types
    getPremisesTypes: async (orgCategoryId = '', search = '') => {
        try {
            const params = {};
            if (orgCategoryId) params.org_category_id = orgCategoryId;
            if (search) params.search = search;

            const response = await apiRequest.get('/premises-types', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises types');
        }
    },

    // Get cities for dropdown
    getCities: async (countryId, search = '') => {
        try {
            const params = { country_id: countryId };
            if (search) params.search = search;
            const response = await apiRequest.get('/cities', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch cities');
        }
    },

    // Approve or reject organization (Core Admin only) - Sends email notification
    approveOrganization: async (orgId, action, comments = '', salesAgentData = {}) => {
        try {
            const response = await apiRequest.post('/organizations/approve', {
                org_id: orgId,
                action: action, // 'approve' or 'reject'
                comments: comments,
                ...salesAgentData // Include sales agent assignment data
            });
            return response.data;
        } catch (error) {
            throw new Error(error.message || `Failed to ${action} organization`);
        }
    },

    // Get organization categories
    getOrganizationCategories: async (search = '') => {
        try {
            const params = search ? { search } : {};
            const response = await apiRequest.get('/organization-categories', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organization categories');
        }
    },

    // Get countries with dial codes
    getCountriesWithDialCodes: async (search = '') => {
        try {
            const params = search ? { search } : {};
            const response = await apiRequest.get('/countries-dialcodes', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch countries');
        }
    },

    // Create App Admin for organization
    createAppAdmin: async (appAdminData) => {
        try {
            const response = await apiRequest.post('/app-admins', appAdminData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create App Admin');
        }
    },

    // Get organization statistics
    getOrganizationStats: async () => {
        try {
            const response = await apiRequest.get('/organizations/stats');
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organization statistics');
        }
    },

    // Register new organization (Public endpoint)
    registerOrganization: async (registrationData) => {
        try {
            const response = await apiRequest.post('/web/register', registrationData);
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to register organization');
        }
    },

    // Resend verification email
    resendVerification: async (email) => {
        try {
            const response = await apiRequest.post('/resend-verification', { email });
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to resend verification email');
        }
    },

    // Verify email
    verifyEmail: async (token) => {
        try {
            const response = await apiRequest.get(`/verify-email?token=${token}`);
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to verify email');
        }
    },

    // Forgot password
    forgotPassword: async (email) => {
        try {
            const response = await apiRequest.post('/forgot-password', { email });
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to send password reset email');
        }
    },

    // Reset password
    resetPassword: async (token, password) => {
        try {
            const response = await apiRequest.post('/reset-password', {
                token,
                password
            });
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to reset password');
        }
    },

    // Get organization subscription details
    getSubscriptionDetails: async (orgId) => {
        try {
            const response = await apiRequest.get(`/organizations/${orgId}/subscription`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch subscription details');
        }
    },

    // Update organization subscription
    updateSubscription: async (orgId, subscriptionData) => {
        try {
            const response = await apiRequest.put(`/organizations/${orgId}/subscription`, subscriptionData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update subscription');
        }
    },

    // Utility function to convert file to base64
    fileToBase64: (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    },

    // Export organizations data as CSV
    exportOrganizations: async (filters = {}) => {
        try {
            const params = { ...filters, format: 'csv' };
            const response = await apiRequest.get('/organizations/export', {
                params,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `organizations_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            throw new Error(error.message || 'Failed to export organizations data');
        }
    }
};